/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-14
 */

// Add third-party dependencies.
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Link,
  Divider,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';

// Add local dependencies.
import Header from '../../layout/Header';
import { StudioHeader } from '../../navigation/studio';
import {
  useGetStudioQuery,
  useGetInvitationMetricsQuery,
  useGetStudioMembersQuery,
} from '../../../features/studios/studiosApi';
import { MemberStatus } from '../../../types/studioMembers';
import { useStudioPermissions } from '../../../hooks/useStudioPermissions';
import LiveRegion from '../../common/LiveRegion';
import { ARIA_ROLES } from '../../../utils/accessibility';
import StudioUsageCard from '../../subscription/StudioUsageCard';

/**
 * Studio dashboard page component.
 * Displays comprehensive information about a specific studio including
 * basic details, contact information, and member statistics.
 *
 * @example
 * return (
 *   <StudioDashboard />
 * )
 * @returns {React.ReactElement} component.
 */
const StudioDashboard: React.FC = () => {
  // Extract studio ID from URL parameters.
  const { studioId } = useParams<{ studioId: string }>();

  // Fetch studio data from API
  const {
    data: studio,
    isLoading,
    error,
  } = useGetStudioQuery(studioId!, {
    skip: !studioId,
  });

  // Fetch invitation metrics for the studio (with error handling for permission issues)
  const { data: invitationMetrics, error: invitationError } =
    useGetInvitationMetricsQuery(studioId!, {
      skip: !studioId,
    });

  // Check if the invitation metrics error is a permission issue (403 Forbidden)
  const isPermissionError =
    invitationError &&
    'status' in invitationError &&
    invitationError.status === 403;

  // Get total active member count (unfiltered) for accurate member counting
  const { data: totalMembersResponse } = useGetStudioMembersQuery(
    {
      studioId: studioId!,
      search: '', // No search filter for total count
      role: undefined, // No role filter for total count
      status: MemberStatus.ACTIVE, // Only count active members
      page: 1,
      pageSize: 1, // We only need the count, not the actual members
    },
    {
      skip: !studioId,
    }
  );

  // Get user permissions for this studio
  const { permissions, role } = useStudioPermissions();

  // Calculate the correct member count
  const actualMemberCount = totalMembersResponse?.count || 0;

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <StudioHeader loading />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <LiveRegion
            message="Loading studio information, please wait..."
            politeness="POLITE"
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '50vh',
              gap: 2,
            }}
          >
            <CircularProgress size={50} />
            <Typography variant="h6" color="text.secondary">
              Loading studio...
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  // Show error state
  if (error || !studio) {
    return (
      <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
        <Header />
        <StudioHeader studio={null} />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <LiveRegion
            message="Error loading studio information. Please try refreshing the page."
            politeness="ASSERTIVE"
          />
          <Alert severity="error" sx={{ mb: 4 }} role="alert">
            {error && 'status' in error && error.status === 404
              ? 'Studio not found. Please check the URL and try again.'
              : 'Failed to load studio information. Please try refreshing the page.'}
          </Alert>
        </Container>
      </Box>
    );
  }

  const getStudioInitials = (name: string) =>
    name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const getSubscriptionStatusColor = (status: string) => {
    if (status === 'active') return 'success';
    if (status === 'trial') return 'warning';
    return 'default';
  };

  const formatStudioType = (studioType?: string) => {
    switch (studioType) {
      case 'personal':
        return 'Personal Studio';
      case 'business':
        return 'Business Studio';
      case 'organization':
        return 'Organization Studio';
      default:
        return 'Studio';
    }
  };

  const formatTimezone = (timezone?: string) => {
    if (!timezone) return 'Not set';

    // Convert common timezone identifiers to user-friendly names
    const timezoneMap: Record<string, string> = {
      'America/New_York': 'Eastern Time (ET)',
      'America/Chicago': 'Central Time (CT)',
      'America/Denver': 'Mountain Time (MT)',
      'America/Los_Angeles': 'Pacific Time (PT)',
      'America/Phoenix': 'Arizona Time (MST)',
      'America/Anchorage': 'Alaska Time (AKT)',
      'Pacific/Honolulu': 'Hawaii Time (HST)',
      UTC: 'Coordinated Universal Time (UTC)',
    };

    return timezoneMap[timezone] || timezone.replace('_', ' ');
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      <Header />
      <StudioHeader studio={studio} userRole={role} permissions={permissions} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Live region for successful data load */}
        <LiveRegion
          message={`Studio information loaded successfully for ${studio.name}`}
          politeness="POLITE"
        />

        {/* Studio Overview Section */}
        <Box
          role={ARIA_ROLES.MAIN}
          aria-label="Studio dashboard content"
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          {/* Studio Header Card */}
          <Card elevation={2}>
            <CardContent sx={{ p: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                {/* Studio Avatar */}
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    fontSize: '2rem',
                    fontWeight: 600,
                  }}
                  src={studio.logo}
                  alt={`${studio.name} logo`}
                >
                  {getStudioInitials(studio.name)}
                </Avatar>

                {/* Studio Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h4"
                    component="h1"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {studio.name}
                  </Typography>

                  {studio.description && (
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 2, lineHeight: 1.6 }}
                    >
                      {studio.description}
                    </Typography>
                  )}

                  {/* Studio Metadata */}
                  <Box
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}
                  >
                    <Chip
                      icon={<PeopleIcon />}
                      label={`${actualMemberCount} members`}
                      variant="outlined"
                      size="small"
                    />
                    {studio.settings?.timezone && (
                      <Chip
                        icon={<ScheduleIcon />}
                        label={studio.settings.timezone}
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Box>

                  {/* Creation Date */}
                  {studio.createdAt && (
                    <Typography variant="body2" color="text.secondary">
                      Created on{' '}
                      {new Date(studio.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Studio Metrics - Only show if we have data and no permission errors */}
          {invitationMetrics && !isPermissionError && (
            <Card elevation={2}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 3 }}
                >
                  Studio Metrics
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <PeopleIcon
                        sx={{ fontSize: 32, color: 'primary.main', mb: 1 }}
                      />
                      <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        {invitationMetrics.total}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Invitations
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <PendingIcon
                        sx={{ fontSize: 32, color: 'warning.main', mb: 1 }}
                      />
                      <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        {invitationMetrics.pending}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pending
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CheckCircleIcon
                        sx={{ fontSize: 32, color: 'success.main', mb: 1 }}
                      />
                      <Typography variant="h4" sx={{ fontWeight: 600 }}>
                        {invitationMetrics.accepted}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Accepted
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <TrendingUpIcon
                        sx={{ fontSize: 32, color: 'success.main', mb: 1 }}
                      />
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          color: 'success.main',
                        }}
                      >
                        {invitationMetrics.acceptance_rate}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Acceptance Rate
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Usage Information and Contact Details */}
          <Grid container spacing={3}>
            {/* Studio Usage Information */}
            <Grid size={{ xs: 12, md: 6 }}>
              <StudioUsageCard
                studioId={studioId!}
                currentMemberCount={actualMemberCount}
                sx={{ height: '100%' }}
              />
            </Grid>

            {/* Contact Information */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card elevation={1}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    Contact Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    {studio.phone && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <PhoneIcon color="action" />
                        <Link
                          href={`tel:${studio.phone}`}
                          color="primary"
                          underline="hover"
                        >
                          {studio.phone}
                        </Link>
                      </Box>
                    )}

                    {studio.website && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                      >
                        <WebsiteIcon color="action" />
                        <Link
                          href={studio.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="primary"
                          underline="hover"
                          sx={{ wordBreak: 'break-all' }}
                        >
                          {studio.website}
                        </Link>
                      </Box>
                    )}

                    {studio.address && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                        }}
                      >
                        <LocationIcon color="action" sx={{ mt: 0.5 }} />
                        <Typography variant="body1">
                          {typeof studio.address === 'string'
                            ? studio.address
                            : studio.address.street_address ||
                              `${studio.address.city || ''} ${studio.address.state || ''} ${studio.address.postal_code || ''}`.trim()}
                        </Typography>
                      </Box>
                    )}

                    {!studio.phone && !studio.website && !studio.address && (
                      <Typography variant="body2" color="text.secondary">
                        No contact information available
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Studio Details */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card elevation={1}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    Studio Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Studio Type
                      </Typography>
                      <Typography variant="body1">
                        {formatStudioType(
                          studio.studioType || studio.studio_type
                        )}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Timezone
                      </Typography>
                      <Typography variant="body1">
                        {formatTimezone(studio.timezone)}
                      </Typography>
                    </Box>

                    {(studio.createdAt || studio.created_at) && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Established
                        </Typography>
                        <Typography variant="body1">
                          {new Date(
                            studio.createdAt || studio.created_at!
                          ).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </Typography>
                      </Box>
                    )}

                    {studio.subscriptionStatus && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Subscription Status
                        </Typography>
                        <Chip
                          label={studio.subscriptionStatus}
                          size="small"
                          color={getSubscriptionStatusColor(
                            studio.subscriptionStatus
                          )}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

// Export component.
export default StudioDashboard;
